import { API_BASE_URL } from "@/lib/services/configs/global";
import { ApiError } from "@/lib/types/https-error.interface";
import axios, {
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosResponse,
} from "axios";
import {
  handleError,
  networkErrorStrategy,
} from "@/lib/services/http-error-strategies";
import { logout } from "@/lib/helper";

const httpService = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

httpService.interceptors.request.use(
  (config) => {
    const access = localStorage.getItem("agent_access_token");
    if (access) {
      config.headers["Accept"] = "application/json";
      if (config.data instanceof FormData) {
        config.headers["Content-Type"] = "multipart/form-data";
      } else {
        config.headers["Content-Type"] = "application/json;charset=UTF-8";
      }
      config.headers["Authorization"] = `Bearer ${access}`;
    }
    return config;
  },
  (err) => {
    throw err;
  }
);

httpService.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error?.response) {
      const statusCode = error?.response?.status;
      if (statusCode === 401) {
        logout();
      }
      const errorData: ApiError = error.response?.data;
      if (statusCode === 400) {
        // show backend 404 message
        handleError(errorData?.code === 404 ? 400 : errorData?.code, errorData);
      } else {
        console.log(statusCode);
        handleError(statusCode, errorData);
      }
    } else {
      networkErrorStrategy();
    }
  }
);

// CRUD API CALLS

async function apiBase<T>(
  url: string,
  options?: AxiosRequestConfig
): Promise<T> {
  const response: AxiosResponse = await httpService(url, options);
  return response.data as T;
}

async function readData<T>(
  url: string,
  headers?: AxiosRequestHeaders
): Promise<T> {
  const options: AxiosRequestConfig = {
    headers,
    method: "GET",
  };

  return await apiBase<T>(url, options);
}

async function createData<TModel, TResult>(
  url: string,
  data: TModel
): Promise<TResult> {
  const options: AxiosRequestConfig = {
    method: "POST",
    data: data instanceof FormData ? data : JSON.stringify(data),
  };

  return await apiBase<TResult>(url, options);
}

async function updateData<TModel, TResult>(
  url: string,
  data: TModel,
  headers?: AxiosRequestHeaders
): Promise<TResult> {
  const options: AxiosRequestConfig = {
    method: "PUT",
    headers,
    data: JSON.stringify(data),
  };

  return await apiBase<TResult>(url, options);
}

async function deleteData(
  url: string,
  headers?: AxiosRequestHeaders
): Promise<void> {
  const options: AxiosRequestConfig = {
    method: "DELETE",
    headers,
  };

  return await apiBase<void>(url, options);
}

export { readData, createData, updateData, deleteData };
